import { configureStore } from '@reduxjs/toolkit'
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux'

import { apiSlice as bookkeepingApiSlice } from '@taxfix/scouts-books'

import accountant from '@/reducers/accountant'
import addresses from '@/reducers/addresses'
import assessment from '@/reducers/assessment'
import bankAccount from '@/reducers/bank-account'
import capitalGains from '@/reducers/capital-gains'
import charitableDonations from '@/reducers/charitable-donations'
import dividendsAndInterest from '@/reducers/dividends-and-interest'
import employment from '@/reducers/employment'
import employmentBenefits from '@/reducers/employment-benefits'
import files from '@/reducers/files'
import foreignIncome from '@/reducers/foreign-income'
import identityVerifications from '@/reducers/identity-verifications'
import investmentSchemes from '@/reducers/investment-schemes'
import issues from '@/reducers/issues'
import messages from '@/reducers/messages'
import otherIncome from '@/reducers/other-income'
import partner from '@/reducers/partner'
import pensionsAndBenefits from '@/reducers/pensions-and-benefits'
import pensionsContributions from '@/reducers/pensions-contributions'
import productBundleReasons from '@/reducers/product-bundle-reasons'
import productBundles from '@/reducers/product-bundles'
import propertyIncome from '@/reducers/property-income'
import reasons from '@/reducers/reasons'
import referrals from '@/reducers/referrals'
import request from '@/reducers/request'
import selfEmployment from '@/reducers/self-employment'
import selfEmploymentExpense from '@/reducers/self-employment-expense'
import selfEmploymentProfile from '@/reducers/self-employment-profile'
import sources from '@/reducers/sources'
import studentLoan from '@/reducers/student-loan'
import taxConsultationFiles from '@/reducers/tax-consultation-files'
import taxConsultationTopics from '@/reducers/tax-consultation-topics'
import taxConsultations from '@/reducers/tax-consultations'
import taxRegistration from '@/reducers/tax-registration'
import taxRegistrationReasons from '@/reducers/tax-registration-reasons'
import userFiles from '@/reducers/user-files'
import workFromHomeAllowance from '@/reducers/work-from-home-allowance'

import { apiSlice } from './api-slice'
import { incomePlatformsReducer } from './income-platforms'
import { profileReducer } from './profile'

export const store = configureStore({
    reducer: {
        accountant,
        addresses,
        assessment,
        bankAccount,
        capitalGains,
        charitableDonations,
        dividendsAndInterest,
        employment,
        employmentBenefits,
        files,
        foreignIncome,
        identityVerifications,
        incomePlatforms: incomePlatformsReducer,
        investmentSchemes,
        issues,
        messages,
        otherIncome,
        partner,
        pensionsAndBenefits,
        pensionsContributions,
        productBundleReasons,
        productBundles,
        profile: profileReducer,
        propertyIncome,
        reasons,
        referrals,
        request,
        selfEmployment,
        selfEmploymentExpense,
        selfEmploymentProfile,
        sources,
        studentLoan,
        taxConsultationFiles,
        taxConsultationTopics,
        taxConsultations,
        taxRegistration,
        taxRegistrationReasons,
        userFiles,
        workFromHomeAllowance,
        // RTKQ slice
        [apiSlice.reducerPath]: apiSlice.reducer,
        // Bookkeeping RTKQ slice
        [bookkeepingApiSlice.reducerPath]: bookkeepingApiSlice.reducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            // TEC-722: Fix how we use Redux, so we don't have to silence warnings
            // Silences warnings about non-serializable values used in actions (e.g. onSuccess/onError functions in our case)
            serializableCheck: false,
            // Silences warnings about immutable values (e.g. saving native File objects in state)
            immutableCheck: false,
        })
            .concat(apiSlice.middleware)
            .concat(bookkeepingApiSlice.middleware),
})

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
